import React, { useState } from 'react';
import {
    Card,
    Space,
    Typography,
    Button,
    Tag,
    Descriptions,
    Spin,
    Result,
    message,
    Modal,
    Form,
    Select,
    Input,
    InputNumber,
    Table,
} from 'antd';
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    EditOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import {
    useGetChargebackDetailQuery,
    useUpdateChargebackStatusMutation,
} from '../../../services/nestjs-base-api';

const { Title, Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
    prepared: 'gold',
    submitted: 'blue',
    accepted: 'green',
    partially_accepted: 'cyan',
    disputed: 'orange',
    replaced: 'purple',
    withdrawn: 'default',
};

const STATUS_OPTIONS = Object.keys(STATUS_COLORS);

export const ChargebackDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const numericId = id ? parseInt(id, 10) : 0;
    const navigate = useNavigate();
    const intl = useIntl();
    const { user } = useAuth();

    const { data, isLoading, isError } = useGetChargebackDetailQuery(
        { jwtToken: user?.jwtToken || '', id: numericId },
        { skip: !numericId },
    );
    const [updateStatus, { isLoading: updating }] =
        useUpdateChargebackStatusMutation();

    const [statusOpen, setStatusOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<string | undefined>(undefined);
    const [notes, setNotes] = useState<string>('');
    const [credit, setCredit] = useState<number | null>(null);

    const handleStatusUpdate = async () => {
        if (!newStatus) return;
        try {
            await updateStatus({
                jwtToken: user?.jwtToken || '',
                id: numericId,
                status: newStatus,
                resolutionNotes: notes || undefined,
                creditAmountUsd: credit ?? undefined,
            }).unwrap();
            message.success(
                intl.formatMessage({
                    id: 'responseIntegrity.chargebacks.updateStatusSuccess',
                }),
            );
            setStatusOpen(false);
            setNewStatus(undefined);
            setNotes('');
            setCredit(null);
        } catch (e) {
            message.error(
                intl.formatMessage({ id: 'responseIntegrity.toast.error' }),
            );
        }
    };

    const handleDownload = async () => {
        try {
            const res = await fetch(
                `http://172.25.48.1:3501/admin/response-integrity/chargebacks/${numericId}/evidence`,
                {
                    headers: {
                        Authorization: `Bearer ${user?.jwtToken || ''}`,
                    },
                },
            );
            if (!res.ok) throw new Error(String(res.status));
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download =
                res.headers
                    .get('Content-Disposition')
                    ?.match(/filename="?([^";]+)/i)?.[1] ??
                `chargeback-${numericId}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(
                intl.formatMessage({
                    id: 'responseIntegrity.chargebacks.evidenceDownloadFailed',
                }),
            );
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin />
            </div>
        );
    }
    if (isError || !data) {
        return (
            <Result
                status="404"
                title="Chargeback not found"
                extra={
                    <Button onClick={() => navigate('/admin/response-integrity/chargebacks')}>
                        <FormattedMessage id="responseIntegrity.actions.back" />
                    </Button>
                }
            />
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space>
                    <Link to="/admin/response-integrity/chargebacks">
                        <Button icon={<ArrowLeftOutlined />}>
                            <FormattedMessage id="responseIntegrity.actions.back" />
                        </Button>
                    </Link>
                </Space>

                <Card
                    title={
                        <FormattedMessage
                            id="responseIntegrity.chargebacks.detail.title"
                            values={{ id: data.id }}
                        />
                    }
                    extra={
                        <Space>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownload}
                            >
                                <FormattedMessage id="responseIntegrity.chargebacks.action.download" />
                            </Button>
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => setStatusOpen(true)}
                            >
                                <FormattedMessage id="responseIntegrity.chargebacks.updateStatus" />
                            </Button>
                        </Space>
                    }
                >
                    <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                        <Descriptions.Item label="Panel partner">
                            {data.panelPartnerDisplayName || data.panelPartner}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={STATUS_COLORS[data.status] ?? 'default'} style={{ margin: 0 }}>
                                <FormattedMessage
                                    id={`responseIntegrity.chargebacks.status.${data.status}`}
                                />
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Survey">
                            {data.surveyName || `Survey #${data.surveyId}`}
                        </Descriptions.Item>
                        <Descriptions.Item label="Brand">
                            {data.brandName || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Submission method">
                            {data.submissionMethod}
                        </Descriptions.Item>
                        <Descriptions.Item label="Created">
                            {dayjs(data.createdAt).format('YYYY-MM-DD HH:mm')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Submitted">
                            {data.submittedAt
                                ? dayjs(data.submittedAt).format('YYYY-MM-DD HH:mm')
                                : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Resolved">
                            {data.resolvedAt
                                ? dayjs(data.resolvedAt).format('YYYY-MM-DD HH:mm')
                                : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Credit (USD)">
                            {data.creditAmountUsd === null ||
                            data.creditAmountUsd === undefined
                                ? '—'
                                : Number(data.creditAmountUsd).toFixed(2)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Created by">
                            User #{data.createdBy}
                        </Descriptions.Item>
                        {data.resolutionNotes && (
                            <Descriptions.Item
                                label={<FormattedMessage id="responseIntegrity.chargebacks.detail.notes" />}
                                span={2}
                            >
                                {data.resolutionNotes}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </Card>

                <Card
                    title={
                        <FormattedMessage id="responseIntegrity.chargebacks.detail.responses" />
                    }
                >
                    <Table
                        rowKey="id"
                        dataSource={data.responses ?? []}
                        pagination={false}
                        size="small"
                        columns={[
                            {
                                title: 'Response',
                                dataIndex: 'id',
                                render: (id: number) => (
                                    <Link to={`/admin/response-integrity/responses/${id}`}>
                                        #{id}
                                    </Link>
                                ),
                            },
                            {
                                title: 'Submitted',
                                dataIndex: 'submittedAt',
                                render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
                            },
                            {
                                title: 'Quality score',
                                dataIndex: 'qualityScore',
                                align: 'right',
                                render: (v: number | null) =>
                                    v === null || v === undefined
                                        ? '—'
                                        : Number(v).toFixed(1),
                            },
                            {
                                title: 'Tier',
                                dataIndex: 'qualityTier',
                                render: (v: string | null) => v || '—',
                            },
                            {
                                title: 'Reason',
                                dataIndex: 'rejectionReason',
                                render: (v: string | null) =>
                                    v ? (
                                        <FormattedMessage
                                            id={`responseIntegrity.reasons.${v}`}
                                            defaultMessage={v}
                                        />
                                    ) : (
                                        '—'
                                    ),
                            },
                            {
                                title: 'Panel session',
                                dataIndex: 'panelSessionId',
                                render: (v: string | null) =>
                                    v ? <Text code style={{ fontSize: 11 }}>{v}</Text> : '—',
                            },
                        ]}
                    />
                </Card>
            </Space>

            <Modal
                title={
                    <FormattedMessage id="responseIntegrity.chargebacks.updateStatus" />
                }
                open={statusOpen}
                onOk={handleStatusUpdate}
                onCancel={() => setStatusOpen(false)}
                okButtonProps={{ disabled: !newStatus, loading: updating }}
                okText="Update"
            >
                <Form layout="vertical">
                    <Form.Item label="New status" required>
                        <Select
                            value={newStatus}
                            onChange={setNewStatus}
                            placeholder="Select status"
                            options={STATUS_OPTIONS.map((s) => ({
                                value: s,
                                label: intl.formatMessage({
                                    id: `responseIntegrity.chargebacks.status.${s}`,
                                }),
                            }))}
                        />
                    </Form.Item>
                    <Form.Item label="Resolution notes (optional)">
                        <Input.TextArea
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </Form.Item>
                    <Form.Item label="Credit amount (USD, optional)">
                        <InputNumber
                            value={credit}
                            onChange={(v) => setCredit(typeof v === 'number' ? v : null)}
                            style={{ width: '100%' }}
                            min={0}
                            step={0.01}
                            precision={2}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
